import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

// Daily charts for AdInsightsPanel. `rows` is the insights API's `breakdown=day` series
// (one entry per day in the range, including empty days).
const toSeries = (rows = []) =>
  rows.map((row) => ({
    day: String(row.key).slice(5),
    spend: row.spend,
    purchases: row.conversions.purchase,
    clicks: row.clicks,
    product_views: row.conversions.product_view,
    messages: row.conversions.message,
    follows: row.conversions.follow,
  }));

function ChartCard({ title, totals, bg, children }) {
  return (
    <div className={`rounded-2xl p-5 mb-4 ${bg}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-900">{title}</h3>
        {totals?.right && <span className="font-semibold text-sm">{totals.right}</span>}
      </div>
      <ResponsiveContainer width="100%" height={220}>
        {children}
      </ResponsiveContainer>
      {totals?.tiles && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {totals.tiles.map((tile) => (
            <div key={tile.label} className="bg-white rounded-xl p-3">
              <p className="text-xs text-gray-500">{tile.label}</p>
              <p className="text-xl font-bold">{tile.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdReportCharts({ rows, currency }) {
  const data = toSeries(rows);
  const sum = (key) => data.reduce((total, d) => total + (d[key] || 0), 0);
  const totalSpend = sum("spend");

  return (
    <div>
      <h2 className="text-lg font-semibold mb-3">Your Ad Report</h2>

      <ChartCard
        title="Spend & Purchases"
        bg="bg-[#EAF6EE]"
        totals={{
          tiles: [
            { label: "Total Spend", value: `${currency || "$"} ${totalSpend.toFixed(totalSpend && totalSpend < 1 ? 4 : 2)}` },
            { label: "Total Purchases", value: sum("purchases") },
          ],
        }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis yAxisId="spend" />
          <YAxis yAxisId="count" orientation="right" allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Line yAxisId="spend" type="monotone" dataKey="spend" name="Spend" stroke="#0f7a44" strokeWidth={2} dot={false} />
          <Line yAxisId="count" type="monotone" dataKey="purchases" name="Purchases" stroke="#9A1B2F" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>

      <ChartCard
        title="Clicks & Product Views"
        bg="bg-[#FBF6DD]"
        totals={{ right: `Total Clicks: ${sum("clicks")}` }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="clicks" name="Clicks" stroke="#9C8A1E" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="product_views" name="Product views" stroke="#08aeea" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>

      <ChartCard
        title="Engagement (Messages and Follows)"
        bg="bg-[#FBEAEA]"
        totals={{
          tiles: [
            { label: "Total Messages", value: sum("messages") },
            { label: "Total Follows", value: sum("follows") },
          ],
        }}
      >
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="day" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="messages" name="Messages" stroke="#9A1B2F" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="follows" name="Follows" stroke="#2ecf8a" strokeWidth={2} dot={false} />
        </LineChart>
      </ChartCard>
    </div>
  );
}
